import React, { useMemo } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, Title } from "chart.js";

import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { colorHandlerCode } from "../../../utils/colorEnumFormatter";
import { formatHandlerCode, formatCurrencyCompact } from "../../../utils/namesFormater";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FundSumDropMap } from "../../../interfaces/DTO/simulation";
import { ChangesDataUtils } from "../../FeesLab/Reports/ChangesDataUtils";
import SavingsSummary from "../SavingsSummary";

ChartJS.register(ArcElement, ChartTooltip, Legend, Title, ChartDataLabels);

const calculateNormalizedEntropy = (values: number[]): number => {
  const total = values.reduce((sum, val) => sum + val, 0);
  const n = values.length;
  if (total === 0 || n <= 1) return 0;

  const entropy = values.reduce((sum, val) => {
    const p = val / total;
    return p > 0 ? sum - p * Math.log(p) : sum;
  }, 0);

  const maxEntropy = Math.log(n);
  return entropy / maxEntropy;
};

const computeMaxGroups = (total: number): number => {
  if (total < 300_000) return 2;
  if (total < 400_000) return 3;
  if (total < 600_000) return 4;
  if (total < 1_000_000) return 5;
  if (total < 2_000_000) return 6;
  if (total < 3_000_000) return 7;
  if (total < 3_500_000) return 8;
  if (total < 4_000_000) return 9;
  return 10;
};

const hybridDispersionScore = (values: number[]): number => {
  const total = values.reduce((sum, val) => sum + val, 0);
  const entropyScore = calculateNormalizedEntropy(values);
  const n = values.length;
  const maxGroups = computeMaxGroups(total);
  const groupBonus = Math.log(n + 1) / Math.log(maxGroups + 1);
  return Math.round(entropyScore * Math.min(groupBonus, 1) * 100);
};

const DispersionScoreIndicator: React.FC<{ score: number }> = ({ score }) => {
const tooltipText = `
:מדד פיזור מ-0 עד 100
0 — ריכוז מוחלט אצל גורם יחיד
100 — פיזור מושלם בהתאם לציפייה


:שיקולים בחישוב
שוויוניות בין סכומים •
מספר הקבוצות הקיימות •

הציפייה למספר קבוצות תלויה בגובה הסכום הכולל.


—
,מבוסס על מודל אנטרופיה מנורמל
.בתוספת התאמה לגובה הסכום`;

  return (
   <Tooltip
      arrow
      title={
      <Box sx={{ textAlign: "right", fontSize: "0.95rem", lineHeight: 1.8 }}>
      <pre style={{ margin: 0, fontFamily: 'inherit' }}>{tooltipText}</pre>
      </Box>
      }
      >
      <Box display="flex" alignItems="center" ml={2}>
        <Typography variant="subtitle1" color="text.secondary">
          מדד פיזור: {score}
        </Typography>
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
      </Box>
    </Tooltip>
  );
};

interface Props {
  data: MimshakData[];
  storageKey?: string;
  showLegend?: boolean;
  fundSumDropMap?: FundSumDropMap;
}

const MimshakPieChart: React.FC<Props> = ({ data, showLegend = true, fundSumDropMap }) => {
  const sourceData = useMemo(() => {
    if (!fundSumDropMap) return data;
    return data.map((row) => {
      const extra = ChangesDataUtils.getTotalFundAddition(fundSumDropMap[row.policy_number]);
      return extra !== 0 ? { ...row, funds_sum: (row.funds_sum ?? 0) + extra } : row;
    });
  }, [data, fundSumDropMap]);

  const groupedData = useMemo(() => {
    const groupMap = new Map<string, number>();
    for (const row of sourceData) {
      groupMap.set(row.handler_code, (groupMap.get(row.handler_code) ?? 0) + (row.funds_sum ?? 0));
    }
    return Object.fromEntries(groupMap);
  }, [sourceData]);

  if (!groupedData || Object.keys(groupedData).length === 0) return null;

  const keys = Object.keys(groupedData);
  const labels = keys.map((handlerCode) => formatHandlerCode(handlerCode));
  const values = Object.values(groupedData);
  const colors = keys.map((handlerCode) => colorHandlerCode(handlerCode));

  const dispersionScore = hybridDispersionScore(values);

  const allPolicies = sourceData.flatMap(
    (row) => row.data?.products?.[0]?.policies_or_accounts ?? []
  );

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: colors,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: showLegend },
      datalabels: {
        color: "black",
        font: { weight: "bold" as const, size: 14 },
        formatter: (value: number, context: any) => {
          const index = context.dataIndex;
          const rawKey = keys[index];
          return `${formatHandlerCode(rawKey)}\n${formatCurrencyCompact(value)}`;
        },
        clip: true,
      },
    },
  };

  return (
    <Box sx={{ p: 1, height: "100%" }}>
      <Box display="flex" alignItems="center" mb={2} gap={1}>
        <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>
          התפלגות לפי יצרן |
        </Typography>
        {allPolicies.length > 0 && <SavingsSummary policies={allPolicies} />}
      </Box>
 <DispersionScoreIndicator score={dispersionScore} />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", height: "100%" }}>
          <Doughnut data={chartData} options={options} />
        </Box>
      </Box>
    </Box>
  );
};

export default MimshakPieChart;
